import React, { useState, useEffect } from 'react';
import UserForm from './components/UserForm';
import RiskProfile from './components/RiskProfile';
import Login from './components/Login';
import ClientDetails from './components/ClientDetails';
import ContactForm from './components/ContactForm';
import './App.css';

function App() {
  const [showForm, setShowForm] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [currentPage, setCurrentPage] = useState('landing');

  const [logoColor, setLogoColor] = useState('#007bff');
  const [isPartner, setIsPartner] = useState(false);
  const [partnerColorEnabled, setPartnerColorEnabled] = useState(false);

  const [openServiceIndex, setOpenServiceIndex] = useState(null);

  const servicesData = [
    {
      title: 'Comprehensive Wealth Management',
      details: [
        'Quarterly Portfolio Reviews: Mandatory quarterly assessments to evaluate performance, optimize allocation, and maintain goal alignment.',
        'Dedicated Wealth Managers: Skilled, certified professionals with deep understanding of global markets and strategies.',
        'Customized Financial Planning: Tailor-made plans for retirement, education, wealth creation, and tax optimization.'
      ]
    },
    {
      title: 'Investment Advisory',
      details: [
        'Equity & Debt Solutions: Diversified options including Mutual Funds, Bonds, and Specialized Investment Funds (SIF).',
        'Premium Investment Vehicles: Opportunities via Portfolio Management Services (PMS), Alternative Investment Funds (AIF), and Private Equity offerings.',
        'Integrated Investment Approach: Unified strategy across asset classes for consistent long-term growth.'
      ]
    },
    {
      title: 'Risk Management & Insurance Solutions',
      details: [
        'Holistic Protection: Life, Health, and General Insurance tailored to safeguard family and assets.',
        'Crisis Support: End-to-end assistance during unforeseen events for financial continuity and seamless claim processing.'
      ]
    },
    {
      title: 'Financial Planning & Life-Stage Solutions',
      details: [
        'Retirement Planning: Build sustainable income streams for post-retirement lifestyle.',
        'Child Education & Family Goals: Dedicated plans to meet future milestones and aspirations.',
        'Tax & Estate Planning: Optimize tax efficiency and ensure smooth wealth transfer.'
      ]
    },
    {
      title: 'Performance Monitoring & Portfolio Optimization',
      details: [
        'Regular Portfolio Review & Rebalancing: Continuous evaluation and adjustments for optimal performance.',
        'Exit Strategies for Underperformance: Timely reallocation from low-performing schemes.',
        'Goal-Based Exit Planning: Structured exits at maturity or goal completion for seamless transitions.'
      ]
    },
    {
      title: 'Strategic Value Enhancement',
      details: [
        'Personalized Investment Selection: Recommendations aligned with your objectives and risk tolerance.',
        'Market Research & Insights: Ongoing updates to inform decision-making.',
        'Tax Harvesting Strategies: Maximize post-tax returns via tax-loss harvesting and optimization.',
        'Alpha Generation: Enhance returns beyond benchmarks through dynamic allocation and tactical insights.'
      ]
    }
  ];

  const toggleService = (idx) => {
    setOpenServiceIndex(openServiceIndex === idx ? null : idx);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const lt = localStorage.getItem('loginType');
    const userRaw = localStorage.getItem('user');
    const user = userRaw ? JSON.parse(userRaw) : null;
    setIsPartner(!!token && lt === 'Partner' && user?.role === 'partner');
  }, [showLogin, currentPage]);

  useEffect(() => {
    const storedEnabled = localStorage.getItem('partnerColorEnabled');
    const storedColor = localStorage.getItem('logoColor');
    if (storedEnabled !== null) setPartnerColorEnabled(storedEnabled === 'true');
    if (storedColor) setLogoColor(storedColor);
  }, [showLogin, currentPage]);

  const rotatingTexts = [
    'SIMPLIFYING',
    'Personal Wealth Management & Investment Planning',
    'Your Secure Path to Prosperity',
  ];
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((i) => (i + 1) % rotatingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => setShowForm(true);
  const handleCloseForm = () => setShowForm(false);

  const handleLoginClick = () => setShowLogin(true);
  const handleCloseLogin = () => setShowLogin(false);

  const handleGetInTouch = () => setShowContact(true);
  const handleCloseContact = () => setShowContact(false);

  const handleBackToHome = () => setCurrentPage('landing');

  if (currentPage === 'risk-profile') {
    return <RiskProfile />;
  }

  if (currentPage === 'client-details') {
    return <ClientDetails onBack={handleBackToHome} />;
  }

  const logoMaskUrl = `${process.env.PUBLIC_URL}/images/equicet-logo.png.png`;
  const headerMaskedLogoStyle = {
    backgroundColor: logoColor,
    width: 180,
    height: 60,
    display: 'block',
    WebkitMaskImage: `url(${logoMaskUrl})`,
    maskImage: `url(${logoMaskUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
  };
  const footerMaskedLogoStyle = {
    backgroundColor: logoColor,
    width: 140,
    height: 45,
    display: 'block',
    WebkitMaskImage: `url(${logoMaskUrl})`,
    maskImage: `url(${logoMaskUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="logo-container">
          {(isPartner && partnerColorEnabled) ? (
            <div className="wealth-logo" style={headerMaskedLogoStyle} aria-label="EquiDet logo"></div>
          ) : (
            <img
              className="wealth-logo"
              src="/images/equicet-logo.png.png"
              alt="EquiDet logo"
            />
          )}
          <h1 className="app-name">EquiDet</h1>
        </div>
        <div className="nav-links">
          <a href="#about">About</a>
          <a href="#services">Services</a>
          <a href="#performance">Our Milestones</a>
          <a href="#team">Team</a>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              handleGetInTouch();
            }}
          >
            Contact
          </a>
          <button className="login-btn" onClick={handleLoginClick}>Login</button>
          <button className="signup-btn" onClick={handleGetStarted}>Get Started</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="dynamic-text-container">
            {currentTextIndex === 0 && (
              <h1 className="hero-title dynamic-text">
                <span style={{ fontStyle: 'italic', fontWeight: 'bold', color: '#007bff' }}>
                  {rotatingTexts[currentTextIndex]}
                </span>
              </h1>
            )}
            {currentTextIndex === 1 && (
              <h2 className="hero-subtitle dynamic-text">
                {rotatingTexts[currentTextIndex]}
              </h2>
            )}
            {currentTextIndex === 2 && (
              <p className="hero-tagline dynamic-text">
                {rotatingTexts[currentTextIndex]}
              </p>
            )}
          </div>

          <p className="hero-description">
            Backed by advanced analytics and personalized investment strategies for modern investors
          </p>
          <div className="cta-buttons">
            <button className="secondary-btn" onClick={handleGetInTouch}>Get in Touch</button>
            <button className="primary-btn" onClick={handleGetStarted}>Start Investing</button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2>About Us</h2>
          <p className="about-description" style={{fontStyle: 'italic', fontWeight: 'bold'}}>
            We are dedicated to revolutionizing financial planning and investments, making it accessible to everyone.
          </p>
          <p className="about-description">
            As India's Youngest Wealth Manager, our aim is to help investors create the highest possible risk-adjusted returns over the long term by building differentiated ACTIVE investment portfolio strategies that investors cannot generally execute by themselves
          </p>
          <p className="about-description">
            EquiDet InFolio Wealth is a Private Limited company incorporated in May 2023 with an aim to provide complete Wealth Management Services for Individual, Family, Small Business & Corporate. Our wide range of financial products and services provides you a digital platform for investment under one umbrella. When it comes to servicing our clients, we ensure that we go beyond the limit and resolve customer queries on high priority with Zero unsatisfied customers till date. We believe, managing investment is a long-term commitment & a responsible job including next-generation wealth transfer and hence we maintain the trust & Faith of customers by regular engagement in understanding the requirements of all investors onboard.
          </p>
        </div>
      </section>

      {/* Our Aim Section */}
      <section className="our-aim-section">
        <div className="container">
          <h2 style={{textAlign: 'center'}}>Our Aim</h2>
          <div style={{textAlign: 'center', marginTop: '20px'}}>
            <p>To Restructure the Model of Financial planning and become most Trusted Financial services company in the world by providing Ethical, Unbiased and Need based Financial Solutions to every Segment of the Investors.</p>
            <p>To transform every Earner as an Investor, which in turn will contribute towards the financial economy of our Nation.</p>
            <p>Right platform to learn, develop skills in effective work culture & maintain work-life balance with good career growth.</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <h2>Our Services</h2>
          <p className="section-subtitle">
            Explore our end-to-end solutions across wealth management, advisory, protection, planning, performance, and strategic value.
          </p>

          {/* Moved inside container to confine width */}
          <div className="services-grid">
            {servicesData.map((svc, idx) => (
              <div
                className={`service-item service-card ${openServiceIndex === idx ? 'expanded' : ''}`}
                key={idx}
                onClick={() => toggleService(idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleService(idx)}
                aria-expanded={openServiceIndex === idx}
              >
                <div className="service-card-header">
                  <h3>{svc.title}</h3>
                  <span className="chevron" aria-hidden="true">▾</span>
                </div>

                <div className={`service-details ${openServiceIndex === idx ? 'open' : ''}`}>
                  <ul className="service-list">
                    {svc.details.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Milestones Section */}
      <section id="performance" className="milestones-section">
        <div className="container">
          <div className="milestones-header">
            <h2 className="milestones-title">OUR MILESTONES</h2>
            <p className="milestones-intro">
              At EquiDet InFolio Wealth Private Limited, our journey is defined by a vision to redefine wealth management for a new generation. With deep expertise, global qualifications, and a strong client-first philosophy, we have built a platform that combines personalized financial planning, advisory excellence, and technology-enabled solutions to deliver consistent results.
            </p>
          </div>
      
          <div className="milestones-content">
            <h3 className="section-heading">Key Milestones</h3>
      
            <div className="milestones-grid">
              <div className="milestone-item">
                <h4 className="milestone-year">2010 – 2022 | Foundation of Expertise</h4>
                <ul className="milestone-list">
                  <li>Built strong foundations in portfolio construction, risk management, and investor education.</li>
                  <li>Led structured investment advisory and strategic allocation across equity, debt, and alternative assets.</li>
                  <li>Adopted data-driven frameworks to benchmark performance and improve risk-adjusted returns.</li>
                  <li>
                    Established robust goal-based planning frameworks for retirement, education, and wealth preservation.
                    <ul className="sub-list">
                      <li>Systematic Investment Plans (SIPs) for disciplined wealth creation.</li>
                      <li>Dynamic asset allocation for changing market conditions.</li>
                    </ul>
                  </li>
                </ul>
              </div>
      
              <div className="milestone-item">
                <h4 className="milestone-year">2023 – 2024 | Strengthening Expertise & Expanding Offerings</h4>
                <ul className="milestone-list">
                  <li>Expanded our product basket to include Mutual Funds, PMS, SIF, AIFs, Bonds, Life & Health Insurance, Private Equity, Pre-IPO & Unlisted Shares.</li>
                  <li>Adopted a structured Quarterly Portfolio Review and Annual Rebalancing process to ensure client portfolios remain aligned with their evolving goals.</li>
                  <li>Established a research-driven approach, applying 25–30 rigorous parameters to evaluate and select funds and strategies.</li>
                </ul>
              </div>
      
              <div className="milestone-item">
                <h4 className="milestone-year">2024 | Technology Integration & Global Standards</h4>
                <ul className="milestone-list">
                  <li>Introduced a Chief Technology Officer to spearhead digital transformation.</li>
                  <li>Implemented AI-powered insights to deliver next-generation wealth management solutions.</li>
                  <li>Rolled out 100% secure digital onboarding and global transaction systems, ensuring clients worldwide experience seamless and safe interactions.</li>
                </ul>
              </div>
      
              <div className="milestone-item">
                <h4 className="milestone-year">2025 | Growth & Client-Centric Excellence</h4>
                <ul className="milestone-list">
                  <li>Expanded from 2 to 6 professionals, adding specialists in technology, client servicing, and financial strategy.</li>
                  <li>Strengthened our customer-first approach by introducing a dedicated client service manager for faster and personalized assistance.</li>
                  <li>Positioned EquiDet as one of India's youngest, most innovative, and globally aligned wealth management firms, trusted by HNI, UHNI, and NRI families.</li>
                </ul>
              </div>
      
              <div className="milestone-item">
                <h4 className="milestone-year">Looking Ahead</h4>
                <p className="milestone-description">
                  EquiDet InFolio Wealth is committed to being more than just a wealth manager—we are your long-term partner in financial success. By blending global expertise, advanced research, regulatory excellence, and AI-driven innovation, we aim to empower investors across geographies to build, scale, and protect wealth with confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team-section">
        <div className="container">
          <div className="team-header">
            <h2 className="team-title">OUR TEAM</h2>
          </div>
          <p className="section-subtitle">Expert financial professionals dedicated to your investment success</p>
          <div className="team-grid">
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/nagendra-kumar.png" alt="Nagendra Kumar" className="team-image" />
              </div>
              <h3>Nagendra Kumar</h3>
              <p>Founder</p>
            </div>
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/sathya-s.png" alt="Sathya S" className="team-image" />
              </div>
              <h3>Sathya S</h3>
              <p>Co Founder</p>
            </div>
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/shuruti-raj.png" alt="Shuruti Raj" className="team-image" />
              </div>
              <h3>Shuruti Raj</h3>
              <p>Chief Technology Officer</p>
            </div>
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/kriti-sinha.png" alt="Kriti Sinha" className="team-image" />
              </div>
              <h3>Kriti Sinha</h3>
              <p>Customer Service Manager</p>
            </div>
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/sp-priyanka.png" alt="S P PRIYANKA" className="team-image" />
              </div>
              <h3>S P PRIYANKA</h3>
              <p>Business Partner</p>
            </div>
          </div>

          {/* Centered heading like OUR TEAM */}
          <div className="team-header bio-heading">
            <h3 className="team-title team-title-sm">Founder & Wealth Management Expert</h3>
          </div>

          <div className="team-bio">
            <p className="about-description">
              Nagendra Kumar is a seasoned wealth management professional with over 15 years of experience helping HNI, UHNI, and NRI clients achieve their financial goals. He holds an MBA in Finance from GlobalNxt University (Malaysia) and a Post Graduate Diploma in Banking & Finance from Manipal University (Bangalore), along with specialized certifications from Moody’s and multiple NISM regulatory bodies.
            </p>
            <p className="about-description">
              Over the years, Nagendra has held key roles at leading banks including Kotak Mahindra, Standard Chartered, Yes Bank, IDFC First Bank, and IndusInd Bank, where he managed diverse client portfolios and delivered personalized wealth strategies.
            </p>
            <p className="about-description">
              As the Founder of his own wealth management practice, Nagendra focuses on goal-based financial planning, investment and portfolio management, and legacy and succession planning.
            </p>
            <p className="about-description">
              Driven by a client-first philosophy, he combines transparency, regulatory compliance, and digital innovation to provide customized financial solutions that build and preserve long-term wealth.
            </p>
          </div>
        </div>
      </section>

      {/* How We Partner Section */}
      <section className="partnership-section">
        <div className="container">
          <h2>How We Partner With You</h2>
          <p className="section-description">
            We believe in creating personalized investment partnerships where our expertise aligns with your financial goals. 
            Our approach combines professional portfolio management with transparent communication and flexible investment options.
          </p>
          <p className="section-description">
            From individual retirement planning to family wealth preservation, we have the tools and expertise to support 
            investors at every stage of their financial journey.
          </p>
          <div className="partnership-cta" style={{textAlign: 'center', marginTop: '30px'}}>
            <button className="primary-btn" onClick={handleGetInTouch}>Get in Touch</button>
          </div>
        </div>
      </section>

      {/* Contact Section removed */}
      {/* (Previously: <section id="contact" className="contact-section"> ... </section>) */}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-box">
          <div className="footer-content">
            <div className="footer-section">
              <div className="logo-container">
                {(isPartner && partnerColorEnabled) ? (
                  <div className="footer-logo" style={footerMaskedLogoStyle} aria-label="EquiDet logo"></div>
                ) : (
                  <img
                    className="footer-logo"
                    src="/images/equicet-logo.png.png"
                    alt="EquiDet logo"
                  />
                )}
                <h3 className="footer-brand">EquiDet</h3>
              </div>
              <p>Helping you invest today for better tomorrow</p>

              {/* Connect icons under EquiDet logo */}
              <div className="connect-block">
                <ul className="social-icons">
                  <li>
                    <a href="#" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-instagram"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-linkedin-in"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" aria-label="Twitter/X" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-x-twitter"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-facebook-f"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-youtube"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer">
                      <i className="fa-brands fa-whatsapp"></i>
                    </a>
                  </li>
                </ul>

                <div className="store-badges">
                  <a href="#" aria-label="Get it on Google Play" target="_blank" rel="noopener noreferrer">
                    <img
                      className="store-badge"
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                    />
                  </a>
                  <a href="#" aria-label="Download on the App Store" target="_blank" rel="noopener noreferrer">
                    <img
                      className="store-badge"
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on the App Store"
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* other footer columns remain unchanged */}
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#about">About</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#performance">Performance</a></li>
                <li><a href="#team">Team</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact Info</h4>
              <p><strong>EquiDet InFolio Wealth Private Limited</strong></p>
              <p>No- 28/4, Bedia Danga 2nd Lane, New Ballygunge Road, Kolkata - 700039</p>
              <p>Mobile: +91 96747 60910</p>
              <p>Email: investment@equidet.com</p>
            </div>
          </div>
          {/* Compliance badges row (safe <img> usage) */}
          <div className="footer-compliance">
            <a
              className="compliance-item"
              href="https://www.amfiindia.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AMFI Registered Distributor"
            >
              <img
                className="compliance-logo"
                src="/images/amfi.png"
                alt="AMFI logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="compliance-text">
                EquiDet InFolio Wealth Pvt. Ltd. is an AMFI Registered Mutual Fund Distributor having ARN-270866.
              </div>
            </a>

            <a
              className="compliance-item"
              href="https://www.bsestarmf.in/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="BSE Star MF - Secure Payment Gateway"
            >
              <img
                className="compliance-logo"
                src="/images/bse-starmf.png"
                alt="BSE Star MF logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="compliance-text">100% Secure Payment Gateway</div>
            </a>

            <a
              className="compliance-item"
              href="https://invest.nseindia.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="NSE Invest"
            >
              <img
                className="compliance-logo"
                src="/images/nse-mf-platform.png"
                alt="NSE Invest logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="compliance-text">NSE Invest</div>
            </a>

            <a
              className="compliance-item"
              href="https://sectigo.com/ssl-certificates/positivessl"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="SSL Enabled"
            >
              <img
                className="compliance-logo"
                src="/images/positivessl.png"
                alt="PositiveSSL badge"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="compliance-text">SSL Enabled</div>
            </a>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2024 EquiDet. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {showForm && <UserForm onClose={handleCloseForm} onNavigate={setCurrentPage} />}
      {showLogin && <Login onClose={handleCloseLogin} onNavigate={setCurrentPage} />}
      {showContact && <ContactForm onClose={handleCloseContact} />}
    </div>
  );
}

export default App;